import { useCallback, useEffect, useRef, useState } from 'react';
import { SAVE_POLL_INTERVAL_MS, SAVE_POLL_MAX_MS, projectionAdvanced } from './useSystemAirflow';

/**
 * FS-952 — follow a write on a page that reads only the device detail (the
 * Missing tab), by the same rule the Overview follows one through the system
 * airflow response (FS-593, `useSystemAirflow`).
 *
 * `projectionStamp()` is the device's `projection_updated_at` on screen now;
 * the equipment drawer takes it just before it sends a write and hands it to
 * `followWrite(baseline)` once the write lands. The device is then re-read
 * every 3 s for 30 s. Each time its stamp moves past the newest one seen,
 * `onSynced` is called: the first move is usually the write, but the stamp
 * on screen can predate a resync nobody read, so a later move is taken too
 * rather than stopping on one that came before the write. If the stamp never
 * moves (a projection that failed, or an API that sends no stamp),
 * `onSynced` is called once at the end, so the page is never left without a
 * read after the write. A second write restarts it; unmounting ends it.
 */
export function useDeviceProjectionFollow(
  stamp: string | null | undefined,
  refetchDevice: (() => void) | undefined,
  onSynced: () => void,
) {
  const [follow, setFollow] = useState<{ baseline: string | null } | null>(null);
  const stampRef = useRef<string | null>(stamp ?? null);
  stampRef.current = stamp ?? null;
  const refetchRef = useRef(refetchDevice);
  refetchRef.current = refetchDevice;
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;
  /** The newest stamp taken for this write, and whether any was. */
  const seen = useRef<{ last: string | null; advanced: boolean }>({ last: null, advanced: false });

  useEffect(() => {
    if (follow === null) return undefined;
    seen.current = { last: follow.baseline, advanced: false };
    const tick = window.setInterval(() => refetchRef.current?.(), SAVE_POLL_INTERVAL_MS);
    const deadline = window.setTimeout(() => {
      setFollow(null);
      if (!seen.current.advanced) onSyncedRef.current();
    }, SAVE_POLL_MAX_MS);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(deadline);
    };
  }, [follow]);

  useEffect(() => {
    if (follow === null || !projectionAdvanced(stamp, seen.current.last)) return;
    seen.current = { last: stamp ?? seen.current.last, advanced: true };
    onSyncedRef.current();
  }, [stamp, follow]);

  /** The stamp on screen now: a writer's baseline, taken before it sends. */
  const projectionStamp = useCallback((): string | null => stampRef.current, []);

  /** Follow a write sent while `baseline` was on screen. */
  const followWrite = useCallback((baseline: string | null) => setFollow({ baseline }), []);

  return { projectionStamp, followWrite, following: follow !== null };
}
